import { useEffect, useState } from "react";
import api, { fmtDate, fmtMXN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Link2, Trash2, Download, Upload, Paperclip, Trash } from "lucide-react";
import { toast } from "sonner";
import ContractChecklists from "@/components/contracts/ContractChecklists";
import ContractOficios from "@/components/contracts/ContractOficios";
import { DOC_CATEGORIES } from "@/components/legal/CorporateDocuments";

const SUBTABS = [
  { id: "resumen", label: "Resumen" },
  { id: "juridico", label: "Jurídico" },
  { id: "contratacion", label: "Contratación" },
  { id: "tramites", label: "Trámites / Requisitos" },
  { id: "oficios", label: "Oficios y Acuses" },
];
const catLabel = (v) => DOC_CATEGORIES.find((c) => c.value === v)?.label || v;

function SummaryView({ contract }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/contracts/${contract.id}/dossier/summary`).then((r) => setS(r.data)).catch(() => {});
  }, [contract.id]);
  if (!s) return <div className="p-6 text-center text-slate-400">Cargando…</div>;
  const row = (l, v) => (
    <div className="flex justify-between gap-3 py-1 border-b" style={{ borderColor: "rgba(148,163,184,0.08)" }}>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0 pt-0.5">{l}</span>
      <span className="text-sm text-slate-200 text-right">{v || "—"}</span>
    </div>
  );
  const estadoColor = { completo: "#4ade80", en_proceso: "#fbbf24", sin_expediente: "#94a3b8" }[s.estado_documental];
  return (
    <div className="space-y-4" data-testid="dossier-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="panel p-3">
          <div className="label-mono text-[10px]">Cumplimiento documental</div>
          <div className="font-mono text-lg font-bold text-cyan-300" data-testid="dossier-cumplimiento">
            {s.cumplimiento_pct === null ? "—" : `${s.cumplimiento_pct}%`}
          </div>
        </div>
        <div className="panel p-3">
          <div className="label-mono text-[10px]">Estado documental</div>
          <div className="font-mono text-sm font-bold uppercase" style={{ color: estadoColor }}>
            {s.estado_documental.replace("_", " ")}
          </div>
        </div>
        <div className="panel p-3">
          <div className="label-mono text-[10px]">Docs jurídicos vinculados</div>
          <div className="font-mono text-lg font-bold text-slate-200">{s.links.juridico}</div>
        </div>
        <div className="panel p-3">
          <div className="label-mono text-[10px]">Docs de contratación</div>
          <div className="font-mono text-lg font-bold text-slate-200">{(s.links.contratacion || 0) + s.documentos_contrato}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-4">
          <div className="label-mono mb-2">// DATOS DEL CONTRATO (en vivo)</div>
          {row("Contratista", s.contratista)}
          {row("Dependencia", s.dependencia)}
          {row("Programa", (s.programa || "").toUpperCase())}
          {row("No. de contrato", s.contrato)}
          {row("Obra", s.obra)}
          {s.cct && row("C.C.T.", s.cct)}
          {row("Ubicación", s.ubicacion)}
        </div>
        <div className="panel p-4">
          <div className="label-mono mb-2">// IMPORTES, FECHAS Y RESPONSABLES</div>
          {row("Importe (subtotal)", fmtMXN(s.subtotal))}
          {row("IVA", fmtMXN(s.iva))}
          {row("Total", fmtMXN(s.total))}
          {row("Anticipo pactado", s.anticipo_pct ? `${s.anticipo_pct}% + IVA` : "—")}
          {row("Firma", s.fecha_firma ? fmtDate(s.fecha_firma) : "—")}
          {row("Inicio / Término", `${s.fecha_inicio ? fmtDate(s.fecha_inicio) : "—"} → ${s.fecha_termino ? fmtDate(s.fecha_termino) : "—"}`)}
          {row("Representante legal", s.representante_legal ? `${s.representante_legal.name}${s.representante_legal.position ? " · " + s.representante_legal.position : ""}` : "—")}
          {row("Residente de obra", s.residente)}
        </div>
      </div>
      {s.checklists.length > 0 && (
        <div className="panel p-4">
          <div className="label-mono mb-2">// TRÁMITES EN SEGUIMIENTO</div>
          {s.checklists.map((cl) => (
            <div key={cl.id} className="flex items-center gap-3 py-1.5">
              <span className="text-sm text-slate-200 flex-1 truncate">{cl.label}</span>
              {cl.observados > 0 && <span className="text-[10px] font-mono text-amber-300">{cl.observados} observados</span>}
              <div className="w-28 h-2 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.15)" }}>
                <div className="h-full rounded-full" style={{ width: `${cl.pct}%`, background: cl.pct >= 100 ? "#4ade80" : "var(--cyan)" }} />
              </div>
              <span className="font-mono text-xs text-cyan-300 w-12 text-right">{cl.pct}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LinksSection({ contract, section, canManage }) {
  const [links, setLinks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [sel, setSel] = useState("");

  const load = () => api.get(`/contracts/${contract.id}/dossier/links`, { params: { section } })
    .then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (canManage) api.get("/legal/documents").then((r) => setDocs(r.data.filter((d) => d.status === "vigente"))).catch(() => {});
    // eslint-disable-next-line
  }, [contract.id, section]);

  const add = async () => {
    if (!sel) return toast.error("Selecciona un documento");
    try {
      await api.post(`/contracts/${contract.id}/dossier/links`, { corporate_document_id: sel, section });
      toast.success("Documento vinculado (versión congelada)");
      setSel(""); setAdding(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al vincular"); }
  };

  const unlink = async (l) => {
    if (!confirm(`¿Desvincular "${l.document.name}" del expediente? El archivo permanece en Gestión Documental.`)) return;
    try { await api.delete(`/dossier/links/${l.id}`); toast.success("Vínculo eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const download = async (l) => {
    try {
      const res = await api.get(`/legal/documents/${l.document.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data])), "_blank");
    } catch { toast.error("No se pudo descargar"); }
  };

  return (
    <div className="space-y-2" data-testid={`dossier-links-${section}`}>
      {canManage && (
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`dossier-add-link-${section}`} onClick={() => setAdding(!adding)}
            className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
            style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
            <Link2 className="w-3.5 h-3.5" /> Vincular documento corporativo
          </button>
          {adding && (
            <>
              <select data-testid={`dossier-link-select-${section}`} value={sel} onChange={(e) => setSel(e.target.value)} className="input-cyber text-xs flex-1 min-w-[220px]">
                <option value="">— Seleccionar de Gestión Documental —</option>
                {docs.map((d) => <option key={d.id} value={d.id}>{d.name} · {catLabel(d.category)} · v{d.version}</option>)}
              </select>
              <button data-testid={`dossier-link-confirm-${section}`} onClick={add} className="btn-primary px-3 py-1.5 text-xs uppercase"><Plus className="w-3.5 h-3.5" /></button>
              <button onClick={() => setAdding(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </>
          )}
        </div>
      )}
      {links.length === 0 && <div className="text-xs text-slate-500 italic">Sin documentos vinculados. Los archivos viven en Gestión Documental; aquí solo se referencian con su versión.</div>}
      {links.map((l) => (
        <div key={l.id} className="flex items-center gap-2 p-2 rounded-sm" style={{ background: "rgba(15,23,42,0.5)" }} data-testid={`dossier-link-${l.id}`}>
          <Link2 className="w-3.5 h-3.5 text-indigo-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-200 truncate">{l.document.name}</div>
            <div className="text-[10px] font-mono text-slate-500">
              {catLabel(l.document.category)} · v{l.document.version} congelada · sha {String(l.document.sha256 || "").slice(0, 10)} · {fmtDate(l.created_at)} · {l.created_by}
            </div>
          </div>
          <button title="Descargar" onClick={() => download(l)} className="text-cyan-300 hover:text-cyan-100"><Download className="w-4 h-4" /></button>
          {canManage && <button title="Desvincular" onClick={() => unlink(l)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
    </div>
  );
}

export default function ContractDossier({ contract, documents, onUpload, onDeleteDoc, uploading }) {
  const { user } = useAuth();
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const [sub, setSub] = useState("resumen");

  return (
    <div className="space-y-4" data-testid="contract-dossier">
      <div className="flex gap-1 flex-wrap border-b pb-2" style={{ borderColor: "var(--border)" }}>
        {SUBTABS.map((t) => (
          <button key={t.id} data-testid={`dossier-tab-${t.id}`} onClick={() => setSub(t.id)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm ${sub === t.id ? "btn-primary" : "btn-ghost"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {sub === "resumen" && <SummaryView contract={contract} />}

      {sub === "juridico" && (
        <div className="panel p-4">
          <div className="label-mono mb-3">// EXPEDIENTE JURÍDICO — referencias a Gestión Documental (versión congelada al vincular)</div>
          <LinksSection contract={contract} section="juridico" canManage={canManage} />
        </div>
      )}

      {sub === "contratacion" && (
        <div className="space-y-4">
          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">// DOCUMENTOS PARTICULARES DEL CONTRATO (firmado, carátula, fallo, anexos)</div>
              {canManage && (
                <label className="btn-ghost cursor-pointer px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
                  style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
                  <Upload className="w-3.5 h-3.5" /> Subir
                  <input type="file" hidden onChange={(e) => onUpload(e, "anexo")} disabled={uploading} data-testid="dossier-upload-doc" />
                </label>
              )}
            </div>
            {(documents || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">Sin documentos. Sube el contrato firmado, acta de fallo, catálogo, programa…</div>
            ) : (
              <div className="space-y-1.5">
                {documents.map((d) => (
                  <div key={d.file_id} className="flex items-center gap-2 p-2 rounded-sm" style={{ background: "rgba(15,23,42,0.5)" }}>
                    <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <a href={`${api.defaults.baseURL}/contracts/${contract.id}/documents/${d.file_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-sm text-cyan-300 hover:underline truncate flex-1">{d.name}</a>
                    <span className="text-[10px] text-slate-500 font-mono">{((d.size || 0) / 1024).toFixed(0)} KB</span>
                    {canManage && (
                      <button onClick={() => onDeleteDoc(d)} className="text-slate-500 hover:text-red-400"><Trash className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="panel p-4">
            <div className="label-mono mb-3">// REFERENCIAS CORPORATIVAS DE CONTRATACIÓN</div>
            <LinksSection contract={contract} section="contratacion" canManage={canManage} />
          </div>
        </div>
      )}

      {sub === "tramites" && <ContractChecklists contract={contract} />}

      {sub === "oficios" && <ContractOficios contract={contract} />}
    </div>
  );
}
